import { useEffect, useMemo, useState } from 'react';

import { formatPlanPrice, getProduct } from '../subscriptions/catalog';
import { MEMBERSHIP_PLAN_ORDER } from '../subscriptions/purchaseSteps';
import type { MembershipPlanId } from '../subscriptions/types';
import {
  fetchNativeIapProductDetails,
  isIosNativeApp,
  type NativeIapProductDetails,
} from '../utils/nativeCheckoutBridge';

export interface PlanPricing {
  /** Price as it must be displayed next to the buy button. */
  price: string;
  /** Trial terms in Apple's wording when native, ours otherwise. */
  trialLabel: string | null;
  /** True when the numbers came from StoreKit rather than our catalog. */
  fromStoreKit: boolean;
}

function catalogPricing(planId: MembershipPlanId): PlanPricing {
  const product = getProduct(planId);
  if (!product) {
    return { price: '', trialLabel: null, fromStoreKit: false };
  }
  const trialDays = product.trial_days ?? 0;
  return {
    price: formatPlanPrice(product),
    trialLabel: trialDays > 0 ? `${trialDays}-day free trial` : null,
    fromStoreKit: false,
  };
}

function storeKitPricing(
  planId: MembershipPlanId,
  details: NativeIapProductDetails,
): PlanPricing {
  const period = details.subscriptionPeriod ?? getProduct(planId)?.billing_interval;
  return {
    price: period ? `${details.price}/${period}` : details.price,
    trialLabel: details.introductoryOffer ?? null,
    fromStoreKit: true,
  };
}

/**
 * Pricing for each membership plan, preferring StoreKit inside the iOS shell.
 *
 * Apple requires its own localized price and introductory-offer terms on the
 * screen that starts a purchase (our catalog is USD-only and would be wrong in
 * every other storefront), so native values win whenever StoreKit answers.
 * Falls back to the catalog on web and if the bridge is unavailable.
 */
export function useMembershipPlanPricing(): Record<MembershipPlanId, PlanPricing> {
  const [nativeDetails, setNativeDetails] = useState<
    Record<string, NativeIapProductDetails>
  >({});

  useEffect(() => {
    if (!isIosNativeApp()) {
      return undefined;
    }
    let cancelled = false;
    const appleIds = MEMBERSHIP_PLAN_ORDER.map(
      (planId) => getProduct(planId)?.apple_iap_product_id,
    ).filter((value): value is string => Boolean(value));

    void fetchNativeIapProductDetails(appleIds)
      .then((details) => {
        if (!cancelled) {
          setNativeDetails(details);
        }
      })
      .catch(() => {
        // Keep catalog pricing; the buy button still works.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const pricing = {} as Record<MembershipPlanId, PlanPricing>;
    for (const planId of MEMBERSHIP_PLAN_ORDER) {
      const appleId = getProduct(planId)?.apple_iap_product_id;
      const details = appleId ? nativeDetails[appleId] : undefined;
      pricing[planId] = details
        ? storeKitPricing(planId, details)
        : catalogPricing(planId);
    }
    return pricing;
  }, [nativeDetails]);
}
